import i18n from "@/i18n";
/**
 * ExportPayrollButton — Prompt 19B.
 *
 * Manager Payroll Export experience inside Review Hours. Renders one
 * "Export payroll" action; opens a compact dialog that:
 *   • loads the immutable payroll summary (JSON) from the existing
 *     `GET /api/workforce/periods/{id}/payroll-export`
 *   • previews Employee · Worked Hours · Staff Meals · Sick Leave
 *   • offers three real downloads (Human CSV, Excel, Machine CSV V1)
 *
 * NEVER queries live operational data. NEVER performs money math.
 * NEVER adds a navigation item.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { formatYMDDate } from "@/components/workforce/workforceHelpers";

// Prompt 19B §22 — visibility mirrors the backend's authorized set.
export const PAYROLL_EXPORT_AUTHORIZED_ROLES = new Set([
    "restaurant", "restaurant_admin", "corporate", "super_admin",
]);

export const _EXPORT_CTA = "Export payroll";
export const _EXPORT_HINT_UNAVAILABLE =
    "Available after the period is closed";
export const _DIALOG_TITLE = "Payroll export";
export const _DIALOG_DESCRIPTION =
    "Review the payroll summary for this closed period, then download "
    + "the format your workflow needs.";
export const _PREVIEW_LOADING = "Loading payroll summary…";
export const _PREVIEW_ERROR =
    "Could not load the payroll summary. Try again.";
export const _PREVIEW_EMPTY = "No employees in this period.";
export const _WARNING_MEAL_ONE = "1 staff meal answer is missing.";
export const _WARNING_MEAL_MANY =
    "{n} staff meal answers are missing.";
export const _WARNING_MEAL_HINT =
    "Reopen the period to correct missing information.";
export const _DOWNLOAD_PENDING = "Preparing…";
export const _DOWNLOAD_ERROR = "Could not prepare the file. Try again.";
export const _DOWNLOAD_REOPENED_MSG =
    "Period must be closed before payroll export.";

export const _STANDARD_NOT_FINAL_MSG =
    "Payroll hours are not final — new payroll files cannot be created yet.";
export const _STANDARD_STORED_NOT_CURRENT_MSG =
    "This payroll file was created earlier; its hours are no longer current. Reopen and Close the period again.";
export const _SUMMARY_CSV_LABEL = "Payroll Summary CSV";
export const _SUMMARY_CSV_DESC =
    "Simple employee hours, staff meals and sick leave.";
export const _EXCEL_LABEL = "Payroll Excel";
export const _EXCEL_DESC = "Excel workbook for review or accounting.";
export const _MACHINE_CSV_LABEL = "Payroll CSV V1";
export const _MACHINE_CSV_DESC =
    "Structured numeric CSV for payroll software.";
// Prompt TES-B1 §R14 §16 — V2 label surfaces the additive aattotyö
// factual column while existing V1 download stays unchanged.
export const _MACHINE_V2_CSV_LABEL = "Payroll CSV V2";
export const _MACHINE_V2_CSV_DESC =
    "Includes Aattotyö · structured numeric CSV for payroll software.";

const _CLOSE_DIALOG_LABEL = "Close";

// Prompt 19B §17 · §19 — accept backend Content-Disposition filename
// when present; otherwise fall back to a deterministic label.
function _extractBackendFilename(headers, fallback) {
    if (!headers) return fallback;
    const cd = headers["content-disposition"]
        || headers["Content-Disposition"];
    if (!cd || typeof cd !== "string") return fallback;
    const m = /filename="?([^";]+)"?/i.exec(cd);
    return m ? m[1].trim() : fallback;
}

function _triggerBrowserDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

export const _CALC_UNAVAILABLE_MSG =
    "Payroll files cannot be created — TES rules or the holiday calendar are missing for";
export const _CALC_STORED_UNAVAILABLE_MSG =
    "This payroll file was created earlier; some hours in it could not be calculated (rules or holiday calendar missing).";
// Backend 409 detail → explicit block message (null = other 409).
const _blockMessage = (detail) => {
    if (!detail) return null;
    if (detail.code === "STANDARD_QUANTITIES_NOT_FINAL") return i18n.t("wf.ExportPayrollButton._STANDARD_NOT_FINAL_MSG", _STANDARD_NOT_FINAL_MSG);
    if (detail.code === "PAYROLL_CALCULATION_UNAVAILABLE") {
        const names = ((detail.calculation_availability || {}).employees || [])
            .map((e) => e.employee_name || e.employee_id).join(", ");
        return `${i18n.t("wf.ExportPayrollButton._CALC_UNAVAILABLE_MSG", _CALC_UNAVAILABLE_MSG)}: ${names}.`;
    }
    return null;
};
const _standardCode = (err) => _blockMessage(
    err && err.response && err.response.data && err.response.data.detail);

export default function ExportPayrollButton({
    restaurantId, periodId, closeState, user,
}) {
    const authorized = !!(
        user && PAYROLL_EXPORT_AUTHORIZED_ROLES.has(user.role));
    const isClosed = !!(closeState
        && closeState.current_state === "CLOSED");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [summary, setSummary] = useState(null);
    const [previewError, setPreviewError] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [standardBlocked, setStandardBlocked] = useState(null);
    // §18 — one pending action at a time per download button.
    const [pendingAction, setPendingAction] = useState(null);
    const reqIdRef = useRef(0);

    const loadSummary = useCallback(async () => {
        if (!restaurantId || !periodId || !isClosed) return;
        const myReq = ++reqIdRef.current;
        setPreviewLoading(true);
        setPreviewError(false);
        setStandardBlocked(null);
        try {
            const { data } = await api.get(
                `/workforce/periods/${periodId}/payroll-export`,
                { params: { restaurant_id: restaurantId } },
            );
            if (myReq !== reqIdRef.current) return;
            setSummary(data || null);
        } catch (err) {
            if (myReq !== reqIdRef.current) return;
            setSummary(null);
            setPreviewError(true);
            setStandardBlocked(_standardCode(err));
        } finally {
            if (myReq === reqIdRef.current) setPreviewLoading(false);
        }
    }, [restaurantId, periodId, isClosed]);

    useEffect(() => {
        if (dialogOpen) loadSummary();
    }, [dialogOpen, loadSummary]);

    const download = useCallback(async (kind) => {
        if (!restaurantId || !periodId) return;
        if (pendingAction) return;
        setPendingAction(kind);
        const params = { restaurant_id: restaurantId };
        let fmt = "csv";
        let fallbackName;
        if (kind === "summary_csv") {
            fmt = "csv";
            fallbackName = `workforce_payroll_${
                (summary && summary.period_start) || ""}_${
                (summary && summary.period_end) || ""}.csv`;
        } else if (kind === "xlsx") {
            fmt = "xlsx";
            fallbackName = `workforce_payroll_${
                (summary && summary.period_start) || ""}_${
                (summary && summary.period_end) || ""}.xlsx`;
        } else if (kind === "machine_csv") {
            fmt = "csv";
            params.profile = "payroll_v1";
            fallbackName = `miseone_workforce_payroll_v1_${
                (summary && summary.period_start) || ""}_${
                (summary && summary.period_end) || ""}.csv`;
        } else if (kind === "machine_csv_v2") {
            // Prompt TES-B1 §R14 §17 — V2 download reuses the
            // existing Payroll export endpoint via `profile`.
            fmt = "csv";
            params.profile = "payroll_v2";
            fallbackName = `miseone_workforce_payroll_v2_${
                (summary && summary.period_start) || ""}_${
                (summary && summary.period_end) || ""}.csv`;
        }
        try {
            const res = await api.get(
                `/workforce/periods/${periodId}/payroll-export/${fmt}`,
                { params, responseType: "blob" },
            );
            const filename = _extractBackendFilename(
                res.headers, fallbackName);
            const blob = res.data instanceof Blob
                ? res.data
                : new Blob([res.data]);
            _triggerBrowserDownload(blob, filename);
        } catch (err) {
            const status = err && err.response
                ? err.response.status : undefined;
            let blockMsg = status === 409 ? _standardCode(err) : null;
            if (status === 409 && err.response.data instanceof Blob) {
                try {
                    const body = JSON.parse(await err.response.data.text());
                    blockMsg = _blockMessage(body && body.detail);
                } catch (_e) { /* not JSON */ }
            }
            if (blockMsg) {
                toast.error(blockMsg);
            } else if (status === 409) {
                toast.error(i18n.t("wf.ExportPayrollButton._DOWNLOAD_REOPENED_MSG", _DOWNLOAD_REOPENED_MSG));
            } else {
                toast.error(i18n.t("wf.ExportPayrollButton._DOWNLOAD_ERROR", _DOWNLOAD_ERROR));
            }
        } finally {
            setPendingAction(null);
        }
    }, [restaurantId, periodId, pendingAction, summary]);

    if (!authorized) return null;
    if (!periodId) return null;

    const missingMeals = Number(
        (summary && summary.meal_answers_missing) || 0);
    const rows = (summary && Array.isArray(summary.rows))
        ? summary.rows : [];
    const rangeText = summary && summary.period_start
        ? `${formatYMDDate(summary.period_start)} \u2013 `
          + `${formatYMDDate(summary.period_end)}`
        : "";

    return (
        <span
            data-testid="workforce-payroll-export-control"
            data-close-state={isClosed ? "CLOSED" : "OPEN"}
            className="inline-flex items-center gap-2"
        >
            <Button
                variant="outline"
                size="sm"
                onClick={() => setDialogOpen(true)}
                disabled={!isClosed}
                data-testid="workforce-payroll-export-btn"
                title={isClosed ? i18n.t("wf.ExportPayrollButton._EXPORT_CTA", _EXPORT_CTA)
                    : i18n.t("wf.ExportPayrollButton._EXPORT_HINT_UNAVAILABLE", _EXPORT_HINT_UNAVAILABLE)}
            >
                {i18n.t("wf.ExportPayrollButton._EXPORT_CTA", _EXPORT_CTA)}
            </Button>
            {!isClosed && (
                <span
                    data-testid="workforce-payroll-export-hint"
                    className="text-xs text-neutral-400"
                >
                    {i18n.t("wf.ExportPayrollButton._EXPORT_HINT_UNAVAILABLE", _EXPORT_HINT_UNAVAILABLE)}
                </span>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent
                    className="max-w-2xl"
                    data-testid="workforce-payroll-export-dialog"
                >
                    <DialogHeader>
                        <DialogTitle
                            data-testid="workforce-payroll-export-title"
                        >
                            {i18n.t("wf.ExportPayrollButton._DIALOG_TITLE", _DIALOG_TITLE)}
                        </DialogTitle>
                        <DialogDescription
                            data-testid="workforce-payroll-export-description"
                        >
                            {i18n.t("wf.ExportPayrollButton._DIALOG_DESCRIPTION", _DIALOG_DESCRIPTION)}
                        </DialogDescription>
                    </DialogHeader>
                    {rangeText && (
                        <div
                            data-testid="workforce-payroll-export-range"
                            className="text-sm text-neutral-600"
                        >
                            {rangeText}
                        </div>
                    )}
                    {missingMeals > 0 && (
                        <div
                            data-testid="workforce-payroll-export-meal-warning"
                            className="rounded-md border border-amber-200
                                        bg-amber-50 px-3 py-2 text-sm
                                        text-amber-800"
                        >
                            <div>
                                {missingMeals === 1
                                    ? i18n.t("wf.ExportPayrollButton._WARNING_MEAL_ONE", _WARNING_MEAL_ONE)
                                    : i18n.t("wf.ExportPayrollButton._WARNING_MEAL_MANY", _WARNING_MEAL_MANY).replace(
                                        "{n}", String(missingMeals))}
                            </div>
                            <div className="text-xs text-amber-700">
                                {i18n.t("wf.ExportPayrollButton._WARNING_MEAL_HINT", _WARNING_MEAL_HINT)}
                            </div>
                        </div>
                    )}
                    <div
                        data-testid="workforce-payroll-export-preview"
                        className="rounded-md border border-neutral-200"
                    >
                        {previewLoading && (
                            <div
                                data-testid="workforce-payroll-export-preview-loading"
                                className="p-4 text-sm text-neutral-500"
                            >
                                {i18n.t("wf.ExportPayrollButton._PREVIEW_LOADING", _PREVIEW_LOADING)}
                            </div>
                        )}
                        {!previewLoading && previewError && (
                            <div
                                data-testid="workforce-payroll-export-preview-error"
                                className="p-4 text-sm text-red-700"
                            >
                                {standardBlocked || i18n.t("wf.ExportPayrollButton._PREVIEW_ERROR", _PREVIEW_ERROR)}
                            </div>
                        )}
                        {!previewLoading && summary && summary.standard_validity
                            && summary.standard_validity.payroll_quantities
                                !== "VALID" && (
                            <div
                                data-testid="workforce-payroll-export-standard-not-current"
                                className="m-3 rounded-md border border-amber-200
                                            bg-amber-50 px-3 py-2 text-sm
                                            text-amber-800"
                            >
                                {i18n.t("wf.ExportPayrollButton._STANDARD_STORED_NOT_CURRENT_MSG", _STANDARD_STORED_NOT_CURRENT_MSG)}
                            </div>
                        )}
                        {!previewLoading && summary && summary.calculation_availability
                            && summary.calculation_availability.status
                                === "UNAVAILABLE" && (
                            <div
                                data-testid="workforce-payroll-export-calc-unavailable"
                                className="m-3 rounded-md border border-amber-200
                                            bg-amber-50 px-3 py-2 text-sm
                                            text-amber-800"
                            >
                                {i18n.t("wf.ExportPayrollButton._CALC_STORED_UNAVAILABLE_MSG", _CALC_STORED_UNAVAILABLE_MSG)}
                            </div>
                        )}
                        {!previewLoading && !previewError
                            && rows.length === 0 && summary && (
                            <div
                                data-testid="workforce-payroll-export-preview-empty"
                                className="p-4 text-sm text-neutral-500"
                            >
                                {i18n.t("wf.ExportPayrollButton._PREVIEW_EMPTY", _PREVIEW_EMPTY)}
                            </div>
                        )}
                        {!previewLoading && !previewError
                            && rows.length > 0 && (
                            <div className="overflow-x-auto">
                                <table
                                    className="w-full text-sm"
                                    data-testid="workforce-payroll-export-preview-table"
                                >
                                    <thead className="bg-neutral-50">
                                        <tr className="text-left
                                            text-xs uppercase
                                            text-neutral-500">
                                            <th className="px-3 py-2">
                                                {i18n.t("wf.exportPayrollButton.employee", "Employee")}
                                            </th>
                                            <th className="px-3 py-2">
                                                {i18n.t("wf.exportPayrollButton.workedHours", "Worked Hours")}
                                            </th>
                                            <th className="px-3 py-2">
                                                {i18n.t("wf.exportPayrollButton.staffMeals", "Staff Meals")}
                                            </th>
                                            <th className="px-3 py-2">
                                                {i18n.t("wf.exportPayrollButton.sickLeave", "Sick Leave")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r, i) => (
                                            <tr
                                                key={r.employee_id || i}
                                                data-testid="workforce-payroll-export-preview-row"
                                                data-employee-id={r.employee_id || ""}
                                                className="border-t
                                                    border-neutral-100"
                                            >
                                                <td className="px-3 py-2">
                                                    {r.employee_name || ""}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {r.worked_hours_display || ""}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {Number(
                                                        r.staff_meal_count || 0)}
                                                </td>
                                                <td className="px-3 py-2">
                                                    {r.sick_leave || "\u2014"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                    <div
                        data-testid="workforce-payroll-export-actions"
                        className="grid gap-2 sm:grid-cols-2"
                    >
                        <ExportOptionButton
                            testId="workforce-payroll-export-summary-csv"
                            label={i18n.t("wf.ExportPayrollButton._SUMMARY_CSV_LABEL", _SUMMARY_CSV_LABEL)}
                            description={i18n.t("wf.ExportPayrollButton._SUMMARY_CSV_DESC", _SUMMARY_CSV_DESC)}
                            pending={pendingAction === "summary_csv"}
                            disabled={!!pendingAction}
                            onClick={() => download("summary_csv")}
                        />
                        <ExportOptionButton
                            testId="workforce-payroll-export-xlsx"
                            label={i18n.t("wf.ExportPayrollButton._EXCEL_LABEL", _EXCEL_LABEL)}
                            description={i18n.t("wf.ExportPayrollButton._EXCEL_DESC", _EXCEL_DESC)}
                            pending={pendingAction === "xlsx"}
                            disabled={!!pendingAction}
                            onClick={() => download("xlsx")}
                        />
                        <ExportOptionButton
                            testId="workforce-payroll-export-machine-csv"
                            label={i18n.t("wf.ExportPayrollButton._MACHINE_CSV_LABEL", _MACHINE_CSV_LABEL)}
                            description={i18n.t("wf.ExportPayrollButton._MACHINE_CSV_DESC", _MACHINE_CSV_DESC)}
                            pending={pendingAction === "machine_csv"}
                            disabled={!!pendingAction}
                            onClick={() => download("machine_csv")}
                        />
                        <ExportOptionButton
                            testId="workforce-payroll-export-machine-csv-v2"
                            label={i18n.t("wf.ExportPayrollButton._MACHINE_V2_CSV_LABEL", _MACHINE_V2_CSV_LABEL)}
                            description={i18n.t("wf.ExportPayrollButton._MACHINE_V2_CSV_DESC", _MACHINE_V2_CSV_DESC)}
                            pending={pendingAction === "machine_csv_v2"}
                            disabled={!!pendingAction}
                            onClick={() => download("machine_csv_v2")}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDialogOpen(false)}
                            data-testid="workforce-payroll-export-close"
                        >
                            {i18n.t("wf.ExportPayrollButton._CLOSE_DIALOG_LABEL", _CLOSE_DIALOG_LABEL)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </span>
    );
}


function ExportOptionButton({
    testId, label, description, pending, disabled, onClick,
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            data-pending={pending ? "true" : "false"}
            className="rounded-md border border-neutral-200 bg-white
                       px-3 py-2 text-left transition
                       hover:border-neutral-300 hover:bg-neutral-50
                       disabled:opacity-60 disabled:cursor-not-allowed"
        >
            <div className="text-sm font-medium text-neutral-900">
                {label}
            </div>
            <div className="text-xs text-neutral-500">
                {pending ? _DOWNLOAD_PENDING : description}
            </div>
        </button>
    );
}
